import { useCallback, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Textarea,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import { AddRegular } from '@fluentui/react-icons';
import { useCreateFoundryAgent, useResolvedFoundryEndpoint } from '@/hooks/useFoundryAgents';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { telemetryService } from '@/services/telemetryService';
import { DeploymentField } from '../compact/DeploymentField';

const useStyles = makeStyles({
  createRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  dialogFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

export interface FoundryAgentCreateButtonProps {
  rawEndpoint?: string | undefined;
  connectionResourceId?: string | undefined;
}

export function FoundryAgentCreateButton({
  rawEndpoint,
  connectionResourceId,
}: FoundryAgentCreateButtonProps) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [model, setModel] = useState('');
  const [instructions, setInstructions] = useState('');
  const { resolvedEndpoint } = useResolvedFoundryEndpoint(
    rawEndpoint,
    connectionResourceId
  );
  const createAgent = useCreateFoundryAgent(resolvedEndpoint ?? '');
  const setParameters = useNodeEditorStore((state) => state.setParameters);
  const canCreate = !!resolvedEndpoint && name.trim().length > 0 && model.trim().length > 0;

  const handleCreate = useCallback(async () => {
    if (!canCreate) return;

    try {
      const agent = await createAgent.mutateAsync({
        name: name.trim(),
        model: model.trim(),
        instructions: instructions.trim(),
      });
      setParameters({
        foundryAgentId: agent.name ?? agent.id,
        foundryAgentName: agent.name ?? name.trim(),
        foundryAgentVersion: 'v2',
        agentModelType: 'FoundryAgentServiceV2',
        foundryVersionName: agent.version ?? '',
        deploymentId: agent.model,
        _systemMessage: agent.instructions ?? '',
      });
      telemetryService.trackEvent('foundry_agent.created');
      setOpen(false);
      setName('');
      setModel('');
      setInstructions('');
    } catch (error) {
      telemetryService.trackException(
        error instanceof Error ? error : new Error(String(error)),
        { context: 'foundry_agent.create' }
      );
    }
  }, [
    canCreate,
    createAgent,
    instructions,
    model,
    name,
    setParameters,
  ]);

  return (
    <div className={styles.createRow}>
      <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
          <DialogTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              icon={<AddRegular />}
              disabled={!resolvedEndpoint}
            >
              Create new agent
            </Button>
          </DialogTrigger>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Create new agent</DialogTitle>
              <DialogContent className={styles.dialogFields}>
                <Field label="Agent name" required>
                  <Input
                    aria-label="Agent name"
                    autoComplete="off"
                    value={name}
                    onChange={(_, data) => setName(data.value)}
                    autoFocus
                  />
                </Field>
                <DeploymentField
                  connectionReferenceName={undefined}
                  {...(connectionResourceId
                    ? { resourceIdOverride: connectionResourceId }
                    : {})}
                  value={model}
                  onChange={(deployment) => setModel(deployment.name)}
                  label="Model"
                  required
                  allowCustomValue={false}
                />
                <Field label="Instructions">
                  <Textarea
                    aria-label="Instructions"
                    value={instructions}
                    onChange={(_, data) => setInstructions(data.value)}
                    resize="vertical"
                  />
                </Field>
                {createAgent.error && (
                  <MessageBar intent="error">
                    <MessageBarBody>{createAgent.error.message}</MessageBarBody>
                  </MessageBar>
                )}
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="secondary">Cancel</Button>
                </DialogTrigger>
                <Button
                  appearance="primary"
                  onClick={handleCreate}
                  disabled={!canCreate || createAgent.isPending}
                  {...(createAgent.isPending ? { icon: <Spinner size="tiny" /> } : {})}
                >
                  Create
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
      </Dialog>
    </div>
  );
}
